import SftGlobalData from './SftGlobalData';

/**
 * Display-only rewrite of images and videos that live on a hoster preview host
 * (….webhosting.be).
 *
 * Such a host serves a self-signed certificate until the real domain is live.
 * The admin and the Unlayer editor iframe are https pages, and browsers
 * auto-upgrade an http:// image there to https:// -- which then fails on the
 * certificate, so the image shows broken although the shop has it. Auth's
 * webshop-asset.php fetches it server-side and serves it from a valid cert
 * (videos too, with Range support so they can seek).
 *
 * toPreviewSafe() swaps such media URLs for the passthrough; fromPreviewSafe()
 * puts the ORIGINAL string back verbatim (it travels url-encoded in `u`), so a
 * saved design never contains the passthrough URL. Round trip is lossless:
 * anything after the media URL (e.g. `&b=2` of a query) is left in place and
 * comes back unchanged.
 */

// scheme optional (protocol-relative //host/...), path must end in a media
// extension; stops before quotes, whitespace, <>, ), backslash (JSON-escaped
// quote) and & (query separator / HTML entity)
const PREVIEW_MEDIA_RE = /(?:https?:)?\/\/(?:[a-z0-9-]+\.)*webhosting\.be(?::\d+)?\/[^\s"'<>)\\&?#]*\.(?:jpe?g|png|gif|webp|avif|svg|bmp|ico|mp4|webm|m4v|mov|ogv)(?:\?[^\s"'<>)\\&#]*)?/gi;

// One source for the passthrough address: the auth base the admin talks to.
// A saved design never contains it (fromPreviewSafe runs on every way out), so
// only the base of THIS session has to be recognised on the way back.
const passthroughBase = () => `${SftGlobalData.baseURL_auth}webshop-asset.php?u=`;

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const passthroughRe = () => new RegExp(`${escapeRegExp(passthroughBase())}([^\\s"'<>)\\\\&]+)`, 'gi');

export const hasPreviewHostImage = (str) => typeof str === 'string' && /webhosting\.be/i.test(str);

export const toPreviewSafe = (str) => {
    if (!hasPreviewHostImage(str)) return str;
    const base = passthroughBase();
    // the exact original goes into `u` (a protocol-relative //host/... too:
    // webshop-asset.php adds a scheme itself), so the way back is verbatim
    return str.replace(PREVIEW_MEDIA_RE, (url) => base + encodeURIComponent(url));
};

export const fromPreviewSafe = (str) => {
    if (typeof str !== 'string' || str.indexOf('webshop-asset.php') === -1) return str;
    return str.replace(passthroughRe(), (whole, encoded) => {
        try {
            return decodeURIComponent(encoded);
        } catch {
            return whole;
        }
    });
};

/**
 * For a single src / background URL in an admin preview (<img>, <video>,
 * css url()). blob:/data: previews of a fresh upload and URLs on any other
 * host come back untouched. Display only -- never store the result.
 */
export const previewSafeUrl = (url) => (typeof url === 'string' ? toPreviewSafe(url) : url);

/** Same, for a design object (Unlayer JSON). Returns the input when untouched. */
const mapDesign = (design, fn) => {
    if (!design || typeof design !== 'object') return design;
    try {
        const json = JSON.stringify(design);
        const out = fn(json);
        return out === json ? design : JSON.parse(out);
    } catch {
        return design;
    }
};

export const designToPreviewSafe = (design) => mapDesign(design, toPreviewSafe);
export const designFromPreviewSafe = (design) => mapDesign(design, fromPreviewSafe);
